import React from 'react';
import { BloodworkAnalysis, FurtherConsiderations, Section } from '../../models';
import {
  HOLOBIOME_KITS,
  HolobiomeKit,
  normalizeSupplementName,
} from '../../models/holobiomeKits';
import { SECTION_HEADINGS } from './supplements-summary-panel';

// Short section labels for the "from:" links under a recommended product —
// matches the compact labels the Results Overview buttons use (e.g. "Immune",
// "Glucose") rather than the long SECTION_HEADINGS labels, which are too wide
// for a 255px kit card. Falls back to the long label for any section not listed.
const SECTION_SHORT_LABEL: Partial<Record<Section, string>> = {
  [Section.RBC]: 'Blood Oxygen',
  [Section.IRON]: 'Iron',
  [Section.WBC]: 'Immune',
  [Section.PLATELETS]: 'Blood Clotting',
  [Section.GLUCOSE]: 'Glucose',
  [Section.KIDNEY]: 'Kidney',
  [Section.PROTEINS]: 'Proteins',
  [Section.NUTRIENTS]: 'Vitamins & Minerals',
  [Section.ELECTROLYTES]: 'Electrolytes',
  [Section.LIVER_BILIARY]: 'Liver',
  [Section.LIPIDS]: 'Lipids',
  [Section.INFLAMMATION_OXIDATIVE_STRESS]: 'Inflammation',
  [Section.THYROID]: 'Thyroid',
  [Section.STRESS_ADRENAL]: 'Stress & Adrenal',
  [Section.SEX_HORMONE]: 'Hormones',
  [Section.PROSTATE]: 'Prostate',
};

// Canonical section order, taken from the Supplements panel's ordering so the
// "from:" links read top-to-bottom the way the report is laid out.
const SECTION_ORDER: Section[] = SECTION_HEADINGS.map(h => h.section);
const sectionShortLabel = (
  section: Section,
): string =>
  SECTION_SHORT_LABEL[section] ??
  SECTION_HEADINGS.find(h => h.section === section)?.label ??
  String(section);

// Fallback Systemic Formulas product page for a Holobiome product whose real
// URL isn't in the supplement catalog yet. The preferred source is the catalog
// link (furtherConsiderations.supplementLinkByName), which carries the sheet's
// exact URL; this slug guess only runs when a product hasn't been wired into the
// catalog. The micro sign is URL-encoded to %c2%b5 (not folded to "u") to match
// the site's own scheme — e.g. "Terra µBiomic" → terra-%c2%b5biomic.
const sfProductUrlFallback = (name: string): string =>
  `https://systemicformulas.com/product/${name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[µμ]/g, '%c2%b5')
    .replace(/[^a-z0-9%-]+/g, '-')
    .replace(/(^-|-$)/g, '')}/`;

// Resolve a product's "learn more" URL: the catalog link from the Supplements
// tab of the sheet first, the slug guess only as a last resort.
const productUrl = (
  name: string,
  furtherConsiderations: FurtherConsiderations,
): string =>
  furtherConsiderations.supplementLinkByName(name) ?? sfProductUrlFallback(name);

// Map each recommended supplement (by normalized display name) to the set of
// sections whose identified patterns recommend it. Same traversal the
// Supplements panel uses (possibleRootCauseCategoriesFor → Supplements
// considerations → resolveSupplement); a product recommended through either its
// primary or its "-2" (section-specific) tag resolves to the same name, and a
// product can be recommended by patterns in more than one section (e.g. EnerGenic
// from both Immune and Glucose), which is exactly why each origin is listed.
export function collectRecommendedSupplementSections(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): Map<string, Section[]> {
  const bySection = new Map<string, Set<Section>>();
  SECTION_HEADINGS.forEach(({ section }) => {
    const categories =
      bloodworkAnalysis.possibleRootCauseCategoriesFor?.(section) ?? [];
    categories.forEach(category => {
      const matches = furtherConsiderations.findConsiderations(
        category.rootCauseCategory,
        section,
        'Supplements',
        brands,
      );
      matches.forEach(consideration => {
        (consideration.items || []).forEach(item => {
          const parsed = furtherConsiderations.resolveSupplement(item);
          if (!parsed?.name) return;
          const key = normalizeSupplementName(parsed.name);
          if (!bySection.has(key)) bySection.set(key, new Set());
          bySection.get(key)!.add(section);
        });
      });
    });
  });
  const ordered = new Map<string, Section[]>();
  bySection.forEach((set, key) =>
    ordered.set(
      key,
      SECTION_ORDER.filter(s => set.has(s)),
    ),
  );
  return ordered;
}

// Thin wrapper: just the set of recommended supplement names (used for
// visibility and by tests).
export function collectRecommendedSupplementNames(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): Set<string> {
  return new Set(
    collectRecommendedSupplementSections(
      bloodworkAnalysis,
      furtherConsiderations,
      brands,
    ).keys(),
  );
}

// Count of kit products that are active for the given recommendation set. The
// panel only has something to show when at least one recommended supplement
// falls inside a kit, so this doubles as the visibility signal.
function activeKitProductCount(activeNames: Set<string>): number {
  let count = 0;
  HOLOBIOME_KITS.forEach(kit => {
    kit.productNames.forEach(name => {
      if (activeNames.has(normalizeSupplementName(name))) count += 1;
    });
  });
  return count;
}

export function hasSupplementProtocolsContent(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): boolean {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return false;
  const active = collectRecommendedSupplementNames(
    bloodworkAnalysis,
    furtherConsiderations,
    brands,
  );
  return activeKitProductCount(active) > 0;
}

interface Props {
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  enabledSupplementBrands?: string[];
  // Jump to a report section. When omitted, the "from:" section origins render
  // as plain text instead of links (e.g. in the PDF).
  onSectionClick?: (section: Section) => void;
}

// A product "box" — a two-line tile the width of a Results Overview button,
// shaped like statusToStyle (border, radius, 12px, same letter-spacing). Line 1
// is the product name; line 2 holds the section(s) that recommended it (empty
// for an inactive product, which keeps every box the same two-line height). An
// ACTIVE (recommended) product wears the kit color like a section button wears
// its status color; an inactive product is grayed out.
const productBoxStyle = (kit: HolobiomeKit, active: boolean): React.CSSProperties => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '1px',
  minHeight: '42px',
  padding: '0.25rem 0.5rem',
  borderRadius: '0.2rem',
  border: `1px solid ${active ? kit.color : '#e9ecef'}`,
  backgroundColor: active ? kit.color : '#e9ecef',
  color: active ? '#fff' : '#adb5bd',
  fontSize: '12px',
  letterSpacing: '0.04em',
  textAlign: 'center',
});

// Line 2 inside an active box: the section(s) that recommended the product,
// each a jump link (white, underlined) when onSectionClick is provided. A
// product can be recommended by more than one section, so all are listed.
function SectionOrigins({
  sections,
  onSectionClick,
}: {
  sections: Section[];
  onSectionClick?: (section: Section) => void;
}): JSX.Element {
  return (
    <div style={{ fontSize: '10.5px', letterSpacing: 'normal', lineHeight: 1.2, opacity: 0.95 }}>
      {sections.map((section, i) => (
        <React.Fragment key={section}>
          {i > 0 && <span> · </span>}
          {onSectionClick ? (
            <span
              role="button"
              tabIndex={0}
              onClick={() => onSectionClick(section)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSectionClick(section);
                }
              }}
              style={{ color: '#fff', cursor: 'pointer', textDecoration: 'underline' }}
            >
              {sectionShortLabel(section)}
            </span>
          ) : (
            <span>{sectionShortLabel(section)}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// One kit card — mirrors a Results Overview group card exactly: 255px wide,
// #fafafa, a 1px header divider with a bold title over a gray subtitle, then a
// full-width column of section-style buttons. Under each recommended product,
// the section(s) that recommended it are listed as jump links.
function KitCard({
  kit,
  sectionsByName,
  furtherConsiderations,
  onSectionClick,
}: {
  kit: HolobiomeKit;
  sectionsByName: Map<string, Section[]>;
  furtherConsiderations: FurtherConsiderations;
  onSectionClick?: (section: Section) => void;
}): JSX.Element {
  return (
    <div
      style={{
        flex: '0 0 255px',
        width: '255px',
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        padding: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        backgroundColor: '#fafafa',
      }}
    >
      <div
        style={{
          borderBottom: '1px solid #dee2e6',
          paddingBottom: '6px',
        }}
      >
        <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#212529' }}>
          Month {kit.month}: {kit.name}
        </div>
        <div style={{ fontSize: '12px', color: '#6c757d', lineHeight: 1.2 }}>
          {kit.subtitle}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'nowrap',
          gap: '6px',
        }}
      >
        {kit.productNames.map(name => {
          const sections = sectionsByName.get(normalizeSupplementName(name));
          const active = !!sections;
          return (
            <div key={name} style={productBoxStyle(kit, active)}>
              <a
                href={productUrl(name, furtherConsiderations)}
                target="_blank"
                rel="noopener noreferrer"
                title={`Learn more about ${name} at Systemic Formulas`}
                style={{ color: 'inherit', textDecoration: 'underline', fontWeight: active ? 'bold' : 'normal' }}
              >
                {name}
              </a>
              {active && sections && sections.length > 0 && (
                <SectionOrigins sections={sections} onSectionClick={onSectionClick} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// "Supplement Protocols" — a Results-Overview-style row of the five Holobiome
// kits. Every product in a kit is listed; a product is highlighted (kit color)
// when a pattern identified for this test recommends it, and grayed otherwise,
// with the recommending section(s) linked beneath it, so the practitioner can
// see at a glance which kits the recommendations cluster into and where they
// came from.
export const SupplementProtocolsPanel: React.FC<Props> = ({
  bloodworkAnalysis,
  furtherConsiderations,
  enabledSupplementBrands,
  onSectionClick,
}) => {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return null;

  const sectionsByName = collectRecommendedSupplementSections(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: '12px',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
      }}
    >
      {HOLOBIOME_KITS.map(kit => (
        <KitCard
          key={kit.key}
          kit={kit}
          sectionsByName={sectionsByName}
          furtherConsiderations={furtherConsiderations}
          onSectionClick={onSectionClick}
        />
      ))}
    </div>
  );
};
